/**
 * What a user is told when the AI Assistant popover fails to generate a
 * response (QA-66).
 *
 * MEASURED in source before this fix: both `handleSubmit` and
 * `handleSubmitMemory` caught every failure — offline, a 429, a 500, a bad
 * request — behind the single blanket toast "Failed to generate AI content.
 * Please try again." That is the exact complaint: "try again or rephrase"
 * with no indication of what failed or what would help.
 *
 * Mirrors the network/server-message split already used in
 * ../../pages/Auth/ForgotPassword/forgotPasswordErrorMessage.js.
 */

/** Shown when the request never reached the server. */
export const AI_PROMPT_NETWORK_ERROR =
  "Couldn't reach the AI service — check your connection and try again.";

/** Shown for a 429 from the AI endpoint. */
export const AI_PROMPT_RATE_LIMIT_ERROR =
  "Too many AI requests right now — wait a moment and try again.";

/** Shown for a 5xx from the AI endpoint. */
export const AI_PROMPT_SERVER_ERROR =
  "The AI service hit an error on our end — try again in a moment.";

/** Shown for a 4xx (other than 429) with no usable server message. */
export const AI_PROMPT_BAD_REQUEST_ERROR =
  "That request couldn't be processed — try shortening or simplifying your prompt.";

/**
 * The message to show for a failed AI Assistant request.
 *
 * Guaranteed: a non-empty, specific sentence — never axios's raw
 * `err.message`, never the same blanket copy for every failure mode.
 *
 * @param {any} err axios error
 * @returns {string}
 */
export function aiPromptErrorMessage(err) {
  if (!err?.response) return AI_PROMPT_NETWORK_ERROR;

  const { status, data } = err.response;

  if (
    data &&
    typeof data === "object" &&
    typeof data.message === "string" &&
    data.message.trim()
  ) {
    return data.message.trim();
  }

  if (status === 429) return AI_PROMPT_RATE_LIMIT_ERROR;
  if (typeof status === "number" && status >= 500) return AI_PROMPT_SERVER_ERROR;
  return AI_PROMPT_BAD_REQUEST_ERROR;
}
